import { Controller } from "@hotwired/stimulus"
import { calculateBookingPricing } from "~/utils/bookingPricing"

export default class extends Controller {
  static values = {
    availabilityUrl: String,
    areaBookingSlug: String,
    enableTax: Boolean,
    taxRate: Number,
    enableSurcharge: Boolean,
    surchargeRate: Number,
    enableInstantPayment: Boolean,
    processingFeePercentage: Number,
    processingFeeFixedCents: Number,
    processingFeePayer: String,
    currency: String,
  }

  static targets = [
    // Form Inputs
    "eventName",
    "date",
    "areaOption",
    "packsContainer",
    "lineItemsContainer",

    // Summary Display
    "summaryEventName",
    "summaryDate",
    "summaryAreaName",
    "summaryPacks",
    "summaryLineItems",
    "summarySubtotal",
    "summarySurcharge",
    "summarySurchargeRate",
    "summarySurchargeRow",
    "summaryTax",
    "summaryTaxRate",
    "summaryTaxRow",
    "summaryProcessingFee",
    "summaryProcessingFeeRow",
    "summaryTotal",

    // Availability
    "availabilityStatus",
  ]

  connect() {
    this.selectedAreaAvailable = null
    this.updateSummary()
    this.initializeAreaSelection()
    requestAnimationFrame(() => this.checkAreaAvailability())

    // Recalculate the summary when a cached menu panel is loaded or shown.
    this.boundHandleMenuChange = this.handleMenuChange.bind(this)
    this.element.addEventListener("menu:loaded", this.boundHandleMenuChange)
  }

  disconnect() {
    this.element.removeEventListener("menu:loaded", this.boundHandleMenuChange)
  }

  handleMenuChange() {
    this.updateSummary()
  }

  prepareLineItemsForSubmit() {
    const selectedRows = Array.from(this.element.querySelectorAll(".line-item-checkbox:checked"))
      .map((checkbox) => checkbox.closest(".line-item-row"))
      .filter(Boolean)
    const rowsByItem = new Map()

    selectedRows.forEach((row) => {
      this.enableBookingItemFields(row)

      const lineItemId = row.dataset.lineItemId
      if (!lineItemId) return

      const quantity = parseInt(row.querySelector(".line-item-quantity")?.value || "0", 10) || 0
      const modifiers = this.readModifierQuantities(row)
      const existingRow = rowsByItem.get(lineItemId)

      if (existingRow) {
        existingRow.quantity += quantity
        this.mergeModifierQuantities(existingRow.modifiers, modifiers)
        this.clearModifierParamFields(row)
        this.disableBookingItemFields(row)
      } else {
        rowsByItem.set(lineItemId, { row, quantity, modifiers })
      }
    })

    Array.from(rowsByItem.values()).forEach(({ row, quantity, modifiers }, index) => {
      this.setBookingItemFieldNames(row, index)

      const quantityField = row.querySelector(".booking-item-quantity-field")
      if (quantityField) quantityField.value = quantity

      this.syncModifierParamFields(row, index, modifiers)
    })
  }

  setBookingItemFieldNames(row, index) {
    const fieldNamesByClassName = {
      "booking-item-line-item-id-field": "line_item_id",
      "booking-item-quantity-field": "quantity",
    }

    Object.entries(fieldNamesByClassName).forEach(([className, attributeName]) => {
      const field = row.querySelector(`.${className}`)
      if (field) {
        field.name = `booking[booking_items_attributes][${index}][${attributeName}]`
      }
    })
  }

  readModifierQuantities(row) {
    const selectedModifiers = {}
    const quantityInputs = row.querySelectorAll(".modifier-quantity-input")

    quantityInputs.forEach((input) => {
      const modifierQuantity = parseInt(input.value, 10) || 0
      if (modifierQuantity > 0) {
        selectedModifiers[input.dataset.modifierId] = modifierQuantity
      }
    })

    return selectedModifiers
  }

  mergeModifierQuantities(target, source) {
    Object.entries(source).forEach(([modifierId, quantity]) => {
      const targetQuantity = parseInt(target[modifierId], 10) || 0
      const sourceQuantity = parseInt(quantity, 10) || 0

      target[modifierId] = targetQuantity + sourceQuantity
    })
  }

  serializeModifierQuantities(selection) {
    return Object.entries(selection)
      .filter(([, quantity]) => (parseInt(quantity, 10) || 0) > 0)
      .sort(([leftId], [rightId]) => String(leftId).localeCompare(String(rightId)))
      .map(([id, quantity]) => ({ id, quantity: parseInt(quantity, 10) || 0 }))
  }

  syncModifierParamFields(row, bookingItemIndex, modifiers) {
    this.clearModifierParamFields(row)

    const fieldsContainer = row.querySelector(".booking-item-fields")
    if (!fieldsContainer) return

    this.serializeModifierQuantities(modifiers).forEach((modifier) => {
      this.appendModifierParamField(fieldsContainer, bookingItemIndex, "id", modifier.id)
      this.appendModifierParamField(
        fieldsContainer,
        bookingItemIndex,
        "quantity",
        modifier.quantity
      )
    })
  }

  appendModifierParamField(fieldsContainer, bookingItemIndex, attributeName, value) {
    const field = document.createElement("input")
    field.type = "hidden"
    field.className = "booking-item-modifier-param-field"
    field.name = `booking[booking_items_attributes][${bookingItemIndex}][modifiers][][${attributeName}]`
    field.value = value
    fieldsContainer.appendChild(field)
  }

  clearModifierParamFields(row) {
    row.querySelectorAll(".booking-item-modifier-param-field").forEach((field) => field.remove())
  }

  enableBookingItemFields(row) {
    row.querySelectorAll(".booking-item-fields input").forEach((field) => {
      field.disabled = false
    })
  }

  disableBookingItemFields(row) {
    row.querySelectorAll(".booking-item-fields input").forEach((field) => {
      field.disabled = true
    })
  }

  updateSummary() {
    // Update Event Name, Date, and Area in summary
    this.summaryEventNameTargets.forEach((target) => {
      target.textContent = this.eventNameTarget.value || "..."
    })

    this.summaryDateTargets.forEach((target) => {
      target.textContent = this.dateTarget.value
        ? new Date(this.dateTarget.value).toLocaleDateString()
        : "..."
    })

    const selectedArea = this.areaOptionTargets.find(
      (radio) => radio.checked || radio.type === "hidden"
    )
    const selectedAreaName = selectedArea
      ? selectedArea.closest(".area-option")?.dataset.areaName
      : "..."

    this.summaryAreaNameTargets.forEach((target) => {
      target.textContent = selectedAreaName
    })

    // Update Packs, Line Items and Total Price
    let totalPrice = 0
    let packsHtml = ""
    let hasPacks = false

    // Handle Packs
    if (this.hasPacksContainerTarget) {
      const selectedPacks = this.packsContainerTarget.querySelectorAll(
        "input[type='checkbox']:checked"
      )

      selectedPacks.forEach((pack) => {
        hasPacks = true
        const packPrice = parseFloat(pack.dataset.packPrice || pack.dataset.price || "0")
        const packName =
          pack.dataset.packName ||
          pack.closest("label")?.querySelector(".label-text")?.textContent?.trim() ||
          "Pack"
        totalPrice += packPrice

        packsHtml += `
          <div class="flex justify-between items-center">
            <span>${packName}</span>
            <span class="font-mono">${this.formatCurrency(packPrice)}</span>
          </div>
        `
      })
    }

    if (!hasPacks) {
      packsHtml = `<p class="text-base-content/60">No packages selected yet.</p>`
    }

    this.summaryPacksTargets.forEach((target) => {
      target.innerHTML = packsHtml
    })

    // Handle Line Items
    let lineItemsHtml = ""
    let hasLineItems = false

    if (this.hasLineItemsContainerTarget) {
      const selectedLineItems = this.lineItemsContainerTargets.flatMap((container) =>
        Array.from(container.querySelectorAll(".line-item-checkbox:checked"))
      )

      selectedLineItems.forEach((checkbox) => {
        hasLineItems = true
        const lineItemRow = checkbox.closest(".line-item-row")
        const quantity = Number(lineItemRow.querySelector(".line-item-quantity").value || 0)
        const unitPrice = parseFloat(lineItemRow.dataset.lineItemPrice || "0")
        const name = lineItemRow.dataset.lineItemName || "Item"

        // Calculate modifier total for this line item
        const modifierTotal = this.calculateModifierTotal(lineItemRow)
        const lineItemTotal = unitPrice * quantity + modifierTotal
        totalPrice += lineItemTotal

        lineItemsHtml += `
          <div class="flex justify-between items-center text-sm">
            <span>${name} (${quantity}x)</span>
            <span class="font-mono">${this.formatCurrency(lineItemTotal)}</span>
          </div>
        `
      })
    }

    if (!hasLineItems) {
      lineItemsHtml = `<p class="text-base-content/60">No menu items selected yet.</p>`
    }

    this.summaryLineItemsTargets.forEach((target) => {
      target.innerHTML = lineItemsHtml
    })

    const pricing = calculateBookingPricing({
      subtotal: totalPrice,
      enableSurcharge: this.enableSurchargeValue,
      surchargeRate: this.surchargeRateValue,
      enableTax: this.enableTaxValue,
      taxRate: this.taxRateValue,
    })

    this.summarySubtotalTargets.forEach((target) => {
      target.textContent = this.formatCurrency(pricing.subtotal)
    })

    this.summarySurchargeRowTargets.forEach((target) => {
      target.style.display = pricing.surchargeEnabled ? "flex" : "none"
    })

    if (pricing.surchargeEnabled) {
      this.summarySurchargeRateTargets.forEach((target) => {
        target.textContent = String(pricing.surchargeRate)
      })

      this.summarySurchargeTargets.forEach((target) => {
        target.textContent = this.formatCurrency(pricing.surcharge)
      })
    }

    this.summaryTaxRowTargets.forEach((target) => {
      target.style.display = pricing.taxEnabled ? "flex" : "none"
    })

    if (pricing.taxEnabled) {
      this.summaryTaxRateTargets.forEach((target) => {
        target.textContent = String(pricing.taxRate)
      })

      this.summaryTaxTargets.forEach((target) => {
        target.textContent = this.formatCurrency(pricing.tax)
      })
    }

    const processingFee = this.calculateProcessingFee(pricing.total)

    this.summaryProcessingFeeRowTargets.forEach((target) => {
      target.style.display = this.customerPaysProcessingFee() ? "flex" : "none"
    })

    if (this.customerPaysProcessingFee()) {
      this.summaryProcessingFeeTargets.forEach((target) => {
        target.textContent = this.formatCurrency(processingFee)
      })
    }

    this.summaryTotalTargets.forEach((target) => {
      target.textContent = this.formatCurrency(
        pricing.total + (this.customerPaysProcessingFee() ? processingFee : 0)
      )
    })
  }

  calculateProcessingFee(subtotal) {
    if (!this.customerPaysProcessingFee() || subtotal <= 0) return 0

    const subtotalCents = Math.round(subtotal * 100)
    const percentageFeeCents = Math.round((subtotalCents * this.processingFeePercentageValue) / 100)

    return (percentageFeeCents + this.processingFeeFixedCentsValue) / 100
  }

  customerPaysProcessingFee() {
    return this.enableInstantPaymentValue && this.processingFeePayerValue === "fan_pays"
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: this.currencyValue || "USD",
    }).format(amount)
  }

  async checkAreaAvailability() {
    const availabilityDate = this.availabilityDateValue()

    if (!availabilityDate) {
      this.clearAreaAvailabilityStatuses()
      return
    }

    this.selectedAreaAvailable = null
    this.renderAreaAvailabilityLoading()

    if (this.hasAvailabilityStatusTarget) {
      this.availabilityStatusTarget.style.display = "block"
    }

    const url = new URL(this.availabilityUrlValue, window.location.origin)
    url.searchParams.append("date", availabilityDate)
    if (this.hasAreaBookingSlugValue) {
      url.searchParams.append("area_booking_slug", this.areaBookingSlugValue)
    }

    try {
      const response = await fetch(url)
      const data = await response.json()

      if (!response.ok) {
        this.renderAreaAvailabilityError(data?.error || "Availability could not be checked")
        return
      }

      this.updateAreaStatuses(data.areas || [])
    } catch (error) {
      console.error("Error checking availability:", error)
      this.renderAreaAvailabilityError("Availability could not be checked")
    } finally {
      if (this.hasAvailabilityStatusTarget) {
        this.availabilityStatusTarget.style.display = "none"
      }
    }
  }

  availabilityDateValue() {
    const value = this.dateTarget.value?.trim()
    if (!value) return null

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value

    const match = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
    if (!match) return value

    const [, month, day, year] = match
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`
  }

  clearAreaAvailabilityStatuses() {
    this.areaOptionTargets.forEach((radio) => {
      const availabilityDiv = radio.closest(".area-option")?.querySelector(".area-availability")
      if (!availabilityDiv) return

      availabilityDiv.style.display = "none"
      availabilityDiv.innerHTML = ""
    })
  }

  renderAreaAvailabilityLoading() {
    this.areaOptionTargets.forEach((radio) => {
      const availabilityDiv = radio.closest(".area-option")?.querySelector(".area-availability")
      if (!availabilityDiv) return

      availabilityDiv.style.display = "block"
      availabilityDiv.innerHTML = '<div class="badge badge-neutral badge-sm">Checking...</div>'
    })
  }

  renderAreaAvailabilityError(message) {
    this.selectedAreaAvailable = false

    this.areaOptionTargets.forEach((radio) => {
      const availabilityDiv = radio.closest(".area-option")?.querySelector(".area-availability")
      if (!availabilityDiv) return

      availabilityDiv.style.display = "block"
      availabilityDiv.innerHTML = `<div class="badge badge-error badge-sm">${message}</div>`
    })
  }

  updateAreaStatuses(areas) {
    this.areaOptionTargets.forEach((radio) => {
      const areaOption = radio.closest(".area-option")
      if (!areaOption) return

      const areaId = areaOption.dataset.areaId
      const areaData = areas.find((area) => String(area.id) === String(areaId))
      const availabilityDiv = areaOption.querySelector(".area-availability")
      const isSelectedArea = radio.checked || radio.type === "hidden"

      if (!areaData) {
        if (availabilityDiv) {
          availabilityDiv.style.display = "block"
          availabilityDiv.innerHTML = '<div class="badge badge-error badge-sm">Unavailable</div>'
        }
        radio.disabled = true
        return
      }

      if (isSelectedArea && areaData) {
        this.selectedAreaAvailable = Boolean(areaData.available)
      }

      if (availabilityDiv) {
        availabilityDiv.style.display = "block"
        if (areaData.available) {
          availabilityDiv.innerHTML = '<div class="badge badge-success badge-sm">Available</div>'
          radio.disabled = false
        } else {
          availabilityDiv.innerHTML = '<div class="badge badge-error badge-sm">Unavailable</div>'
          radio.disabled = true
          if (radio.type === "radio") {
            radio.checked = false
          }
        }
      }
    })
    this.updateSummary() // Re-run summary to reflect any changes
  }

  handleDateChange() {
    this.selectedAreaAvailable = null
    this.updateSummary()
    this.checkAreaAvailability()
  }

  selectArea() {
    this.areaOptionTargets.forEach((radio) => {
      const areaOption = radio.closest(".area-option")
      if (radio.checked) {
        areaOption.classList.add("border-primary", "bg-primary/5")
        areaOption.classList.remove("border-base-300")
      } else {
        areaOption.classList.remove("border-primary", "bg-primary/5")
        areaOption.classList.add("border-base-300")
      }
    })
    this.updateSummary()
  }

  initializeAreaSelection() {
    this.areaOptionTargets.forEach((radio) => {
      if (radio.checked) {
        radio.closest(".area-option").classList.add("border-primary", "bg-primary/5")
        radio.closest(".area-option").classList.remove("border-base-300")
      }
    })
  }

  async paymentValidationMessage() {
    if (!this.dateTarget.value) return "Please select an event date before payment."

    await this.checkAreaAvailability()

    const selectedArea = this.areaOptionTargets.find(
      (radio) => radio.checked || radio.type === "hidden"
    )

    if (!selectedArea || selectedArea.disabled) {
      if (selectedArea?.type === "hidden") {
        return "This area is unavailable on the selected date. Please choose a different date."
      }

      return "Please select an available area before payment."
    }

    if (this.selectedAreaAvailable === false) {
      return "This area is already booked for the selected date. Please choose another date."
    }

    return null
  }

  toggleLineItem(event) {
    const checkbox = event.target
    const lineItemRow = checkbox.closest(".line-item-row")
    const detailsDiv = lineItemRow.querySelector(".line-item-details")
    const bookingItemFields = lineItemRow.querySelectorAll(".booking-item-fields input")
    const minOrderQty = parseInt(lineItemRow.dataset.minOrderQty) || 1

    if (checkbox.checked) {
      // Show quantity controls
      detailsDiv.style.display = "block"
      // Update styling
      lineItemRow.classList.add("border-primary", "bg-primary/5")
      lineItemRow.classList.remove("border-base-300")
      // Enable form fields for submission
      bookingItemFields.forEach((field) => (field.disabled = false))
      // Initialize modifier group states
      const modifierGroups = lineItemRow.querySelectorAll(".modifier-group")
      modifierGroups.forEach((group) => {
        const groupId = group.dataset.groupId
        this.updateModifierGroupUI(lineItemRow, groupId)
      })
    } else {
      // Hide quantity controls
      detailsDiv.style.display = "none"
      // Reset styling
      lineItemRow.classList.remove("border-primary", "bg-primary/5")
      lineItemRow.classList.add("border-base-300")
      // Reset quantity to minimum order quantity
      lineItemRow.querySelector(".line-item-quantity").value = minOrderQty
      // Reset modifier selections
      const modifierCheckboxes = lineItemRow.querySelectorAll(".modifier-checkbox")
      modifierCheckboxes.forEach((checkbox) => {
        checkbox.checked = false
        checkbox.disabled = false
      })
      // Reset modifier group UI states
      const modifierGroups = lineItemRow.querySelectorAll(".modifier-group")
      modifierGroups.forEach((group) => {
        const groupId = group.dataset.groupId
        this.resetModifierGroupUI(lineItemRow, groupId)
      })
      this.clearModifierParamFields(lineItemRow)
      // Disable form fields (they won't be submitted)
      bookingItemFields.forEach((field) => (field.disabled = true))
      // Reset quantity in form field to minimum order quantity
      const quantityField = lineItemRow.querySelector(".booking-item-quantity-field")
      if (quantityField) quantityField.value = minOrderQty
    }

    this.updateSummary()
  }

  updateLineItemTotal(event) {
    const input = event.target
    const lineItemRow = input.closest(".line-item-row")
    const minOrderQty = parseInt(lineItemRow.dataset.minOrderQty) || 1
    let quantity = parseInt(input.value) || minOrderQty
    const unitPrice = parseFloat(lineItemRow.dataset.lineItemPrice)
    const totalSpan = lineItemRow.querySelector(".line-item-total")

    // Validate minimum order quantity
    if (quantity < minOrderQty) {
      quantity = minOrderQty
      input.value = quantity
      // Show validation error message
      this.showQuantityValidationError(input, minOrderQty)
    } else {
      // Remove any existing validation error
      this.removeQuantityValidationError(input)
    }

    // Calculate total including modifiers
    const modifierTotal = this.calculateModifierTotal(lineItemRow)
    const total = unitPrice * quantity + modifierTotal
    totalSpan.textContent = this.formatCurrency(total)

    // Update the quantity in the hidden form field
    const quantityField = lineItemRow.querySelector(".booking-item-quantity-field")
    if (quantityField) {
      quantityField.value = quantity
    }

    lineItemRow.querySelectorAll(".modifier-group").forEach((group) => {
      this.updateModifierGroupUI(lineItemRow, group.dataset.groupId)
    })

    this.updateSummary()
  }

  updateModifierSelection(event) {
    const checkbox = event.target
    const lineItemRow = checkbox.closest(".line-item-row")
    const quantityInput = lineItemRow.querySelector(
      `.modifier-quantity-input[data-modifier-id="${checkbox.dataset.modifierId}"]`
    )
    const nextQuantity = checkbox.checked
      ? Math.max(parseInt(quantityInput?.value || "0") || 0, 1)
      : 0

    this.setModifierQuantity(lineItemRow, checkbox.dataset.modifierId, nextQuantity)

    // Clear any validation highlights when user makes selection
    this.clearModifierGroupHighlights()
  }

  calculateModifierTotal(lineItemRow) {
    const quantityInputs = lineItemRow.querySelectorAll(".modifier-quantity-input")
    const lineItemQuantity = this.lineItemQuantity(lineItemRow)
    let modifierTotal = 0

    quantityInputs.forEach((input) => {
      const modifierPrice = parseFloat(input.dataset.modifierPrice) || 0
      const modifierQuantity = parseInt(input.value, 10) || 0
      const group = input.closest(".modifier-group")
      const groupMultiplier =
        group?.dataset.multiplyLimitsByQuantity === "true" ? 1 : lineItemQuantity
      modifierTotal += modifierPrice * modifierQuantity * groupMultiplier
    })

    return modifierTotal
  }

  recalculateLineItemTotal(lineItemRow) {
    const quantity = this.lineItemQuantity(lineItemRow)
    const unitPrice = parseFloat(lineItemRow.dataset.lineItemPrice)
    const totalSpan = lineItemRow.querySelector(".line-item-total")

    // Calculate total including modifiers
    const modifierTotal = this.calculateModifierTotal(lineItemRow)
    const total = unitPrice * quantity + modifierTotal
    totalSpan.textContent = this.formatCurrency(total)
  }

  showQuantityValidationError(input) {
    // Remove any existing error message
    this.removeQuantityValidationError(input)

    // Add error styling to input
    input.classList.add("input-error")
  }

  removeQuantityValidationError(input) {
    // Remove error styling
    input.classList.remove("input-error")
  }

  updateModifierGroupUI(lineItemRow, groupId) {
    const group = lineItemRow.querySelector(`.modifier-group[data-group-id="${groupId}"]`)
    if (!group) return

    const minAllowed = this.effectiveModifierLimit(lineItemRow, group, group.dataset.minAllowed)
    const maxAllowed = this.effectiveModifierLimit(lineItemRow, group, group.dataset.maxAllowed)
    const limitLabel = group.querySelector(".modifier-group-limit-label")

    if (limitLabel) {
      limitLabel.textContent = this.modifierLimitLabel(minAllowed, maxAllowed)
    }

    // Get all modifiers in this group
    const groupModifiers = lineItemRow.querySelectorAll(
      `.modifier-checkbox[data-group-id="${groupId}"]`
    )
    const totalSelectedQuantity = this.getGroupSelectedQuantity(lineItemRow, groupId)

    // Update UI based on max constraint only
    groupModifiers.forEach((modifier) => {
      const modifierRow = modifier.closest(".p-2")
      const modifierId = modifier.dataset.modifierId
      const quantityInput = lineItemRow.querySelector(
        `.modifier-quantity-input[data-modifier-id="${modifierId}"]`
      )
      const modifierQuantity = parseInt(quantityInput?.value || "0", 10) || 0

      if (quantityInput) {
        quantityInput.disabled = !modifier.checked
      }

      if (
        !Number.isNaN(maxAllowed) &&
        totalSelectedQuantity >= maxAllowed &&
        modifierQuantity <= 0
      ) {
        // Disable and fade unselected modifiers when max reached
        modifier.disabled = true
        modifierRow.classList.add("opacity-50", "pointer-events-none")
      } else {
        // Re-enable modifiers when under max
        modifier.disabled = false
        modifierRow.classList.remove("opacity-50", "pointer-events-none")
      }
    })
  }

  validateRequiredModifierGroups() {
    // Clear any existing highlights
    this.clearModifierGroupHighlights()

    const lineItemRows = this.element.querySelectorAll(".line-item-row")

    for (const row of lineItemRows) {
      const checkbox = row.querySelector(".line-item-checkbox")
      if (!checkbox || !checkbox.checked) continue

      const modifierGroups = row.querySelectorAll(".modifier-group")

      for (const group of modifierGroups) {
        const groupId = group.dataset.groupId
        const selectedCount = this.getGroupSelectedQuantity(row, groupId)
        const minAllowed = this.effectiveModifierLimit(row, group, group.dataset.minAllowed) || 0
        const maxAllowed = this.effectiveModifierLimit(row, group, group.dataset.maxAllowed)

        if (selectedCount === 0) continue

        if (
          selectedCount < minAllowed ||
          (!Number.isNaN(maxAllowed) && selectedCount > maxAllowed)
        ) {
          // Highlight the first invalid group and scroll to it
          this.highlightRequiredGroup(group)
          this.scrollToGroup(group)
          return false
        }
      }
    }

    return true
  }

  highlightRequiredGroup(group) {
    const groupContainer = group.querySelector(".border")
    if (groupContainer) {
      groupContainer.classList.add("border-red-300", "border-2")
      groupContainer.classList.remove("border-top-0")
    }
  }

  clearModifierGroupHighlights() {
    const highlightedGroups = this.element.querySelectorAll(".border-red-300")
    highlightedGroups.forEach((group) => {
      group.classList.remove("border-red-300", "border-2")
      group.classList.add("border-top-0")
    })
  }

  scrollToGroup(group) {
    const menuPanel = group.closest("[data-menu-loader-panel='true']")
    if (menuPanel) {
      menuPanel.dispatchEvent(
        new CustomEvent("menu:show", {
          bubbles: true,
          detail: { menuId: menuPanel.dataset.menuId },
        })
      )
    }

    // Small delay to ensure the step navigation has completed
    setTimeout(() => {
      group.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      })
    }, 300)
  }

  resetModifierGroupUI(lineItemRow, groupId) {
    const group = lineItemRow.querySelector(`.modifier-group[data-group-id="${groupId}"]`)
    if (!group) return

    const groupModifiers = lineItemRow.querySelectorAll(
      `.modifier-checkbox[data-group-id="${groupId}"]`
    )

    groupModifiers.forEach((modifier) => {
      const modifierRow = modifier.closest(".p-2")
      const modifierId = modifier.dataset.modifierId
      const quantityInput = lineItemRow.querySelector(
        `.modifier-quantity-input[data-modifier-id="${modifierId}"]`
      )
      modifier.disabled = false
      modifierRow.classList.remove("opacity-50", "pointer-events-none")

      if (quantityInput) {
        quantityInput.value = 0
        quantityInput.disabled = true
      }
    })
  }

  updateModifierQuantity(event) {
    const input = event.target
    const lineItemRow = input.closest(".line-item-row")
    const modifierId = input.dataset.modifierId
    const sanitizedQuantity = Math.max(parseInt(input.value, 10) || 0, 0)

    this.setModifierQuantity(lineItemRow, modifierId, sanitizedQuantity)
    this.clearModifierGroupHighlights()
  }

  setModifierQuantity(lineItemRow, modifierId, requestedQuantity) {
    const checkbox = lineItemRow.querySelector(
      `.modifier-checkbox[data-modifier-id="${modifierId}"]`
    )
    const quantityInput = lineItemRow.querySelector(
      `.modifier-quantity-input[data-modifier-id="${modifierId}"]`
    )
    if (!checkbox || !quantityInput) return

    const groupId = checkbox.dataset.groupId
    const group = lineItemRow.querySelector(`.modifier-group[data-group-id="${groupId}"]`)
    const maxAllowed = this.effectiveModifierLimit(lineItemRow, group, checkbox.dataset.groupMax)
    const currentQuantity = parseInt(quantityInput.value, 10) || 0
    const groupQuantityExcludingCurrent =
      this.getGroupSelectedQuantity(lineItemRow, groupId) - currentQuantity

    let nextQuantity = Math.max(requestedQuantity, 0)

    if (!Number.isNaN(maxAllowed)) {
      nextQuantity = Math.min(nextQuantity, Math.max(maxAllowed - groupQuantityExcludingCurrent, 0))
    }

    quantityInput.value = nextQuantity
    checkbox.checked = nextQuantity > 0

    this.updateModifierGroupUI(lineItemRow, groupId)
    this.recalculateLineItemTotal(lineItemRow)
    this.updateSummary()
  }

  getGroupSelectedQuantity(lineItemRow, groupId) {
    const quantityInputs = lineItemRow.querySelectorAll(
      `.modifier-quantity-input[data-group-id="${groupId}"]`
    )

    return Array.from(quantityInputs).reduce((total, input) => {
      return total + (parseInt(input.value, 10) || 0)
    }, 0)
  }

  effectiveModifierLimit(lineItemRow, group, limit) {
    const parsedLimit = parseInt(limit, 10)
    if (Number.isNaN(parsedLimit)) return NaN
    if (!group || group.dataset.multiplyLimitsByQuantity !== "true") return parsedLimit

    return parsedLimit * this.lineItemQuantity(lineItemRow)
  }

  modifierLimitLabel(minAllowed, maxAllowed) {
    if (!Number.isNaN(minAllowed) && !Number.isNaN(maxAllowed)) {
      return minAllowed === maxAllowed
        ? `Choose ${minAllowed}`
        : `Choose ${minAllowed}-${maxAllowed}`
    }

    if (!Number.isNaN(minAllowed)) return `Min: ${minAllowed}`
    if (!Number.isNaN(maxAllowed)) return `Max: ${maxAllowed}`

    return ""
  }

  lineItemQuantity(lineItemRow) {
    const quantityInput = lineItemRow.querySelector(".line-item-quantity")
    const minOrderQty = parseInt(lineItemRow.dataset.minOrderQty, 10) || 1
    const quantity = parseInt(quantityInput?.value, 10) || minOrderQty

    return Math.max(quantity, minOrderQty)
  }
}
